import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { AuthCodeWithPkce, AuthCodeWithPkceProps } from 'packages/auth'
import { splitSearchQuery } from 'packages/utils/misc'

import {
  setNeedsFullAuthRedirect,
  setTokens,
} from 'app/fieldapp/store/auth/actions'

import {
  isViewingSharedClean,
  SHARED_CLEAN_ROOT_URL,
} from './components/schedule/schedule.utils'
import {
  getAuthToken,
  getImpersonationToken,
  getNeedsFullAuthRedirect,
  getNeedsSilentRefresh,
  getRefreshToken,
} from './store/auth/selectors'

type PickedProps = Pick<
  AuthCodeWithPkceProps,
  'authClientId' | 'authUrl' | 'onAuthInitialized' | 'tokenUrl'
>
export type FieldAppAuthContainerProps = PickedProps

export const FieldAppAuthContainer: React.FC<FieldAppAuthContainerProps> =
  passThruProps => {
    const dispatch = useDispatch()

    const authToken = useSelector(getAuthToken)
    const impersonationToken = useSelector(getImpersonationToken)
    const refreshToken = useSelector(getRefreshToken)
    const needsFullAuth = useSelector(getNeedsFullAuthRedirect)
    const needsSilentRefresh = useSelector(getNeedsSilentRefresh)

    /*
     * If the current user is a delegate token user viewing a shared clean, we must skip the entire
     * authentication bootstrapping process, and just directly use the delegate token provided in the URL params instead.
     * In this case, we will not render any of the authentication handling components.
     */
    if (isViewingSharedClean()) {
      // since we split the token into query params, we need to re-construct it before sending to store.
      const {
        b: body = '',
        h: header = '',
        s: sig = '',
      } = splitSearchQuery(window.location.search)

      // ensure we have all necessary components to make the token; otherwise we might end up with
      // a token like ".." after joining, which we obviously do not want
      const canCombineToken = !!body && !!header && !!sig
      const delegateToken = canCombineToken ? [header, body, sig].join('.') : ''

      dispatch(
        setTokens({
          accessToken: undefined,
          delegateToken,
          idToken: undefined,
          refreshToken: undefined,
        }),
      )

      passThruProps.onAuthInitialized(SHARED_CLEAN_ROOT_URL)
      return null
    }

    return (
      <AuthCodeWithPkce
        {...passThruProps}
        authToken={authToken}
        isImpersonated={!!impersonationToken}
        needsFullAuth={needsFullAuth}
        needsSilentRefresh={needsSilentRefresh}
        refreshToken={refreshToken}
        setNeedsFullAuthRedirect={setNeedsFullAuthRedirect}
        setTokens={setTokens}
      />
    )
  }
